import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";

import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, Car, Clock, Shield, CheckCircle2, Star } from "lucide-react";
import { generateLocalBusinessSchema } from "@/lib/schemaGenerator";

interface FeaturedPricing {
  route: string;
  price: string;
  note?: string;
}

interface CityServiceTemplateProps {
  cityName: string;
  cityDescription: string;
  metaDescription: string;
  canonicalPath: string;
  heroImage?: string;
  additionalContent?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  cityReviews?: Array<{
    author: string;
    rating: number;
    date: string;
    text: string;
  }>;
  serviceRadius?: string;
  featuredPricing?: FeaturedPricing;
}

const CityServiceTemplate = ({ 
  cityName, 
  cityDescription, 
  metaDescription, 
  canonicalPath, 
  heroImage,
  additionalContent,
  coordinates,
  cityReviews,
  serviceRadius,
  featuredPricing
}: CityServiceTemplateProps) => {
  const services = [
    {
      icon: <Car className="w-6 h-6" />,
      title: "Point-to-Point",
      description: "Direct transportation to any destination"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Hourly Service",
      description: "Flexible transportation for extended needs"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Special Events",
      description: "Weddings, proms, corporate events"
    }
  ];

  // Generate hero image URL for schema
  const heroImageUrl = heroImage 
    ? `https://ricksblackcarservicesantonio.com${heroImage.startsWith('/') ? heroImage : `/${heroImage}`}`
    : "https://ricksblackcarservicesantonio.com/og-image.jpg";

  // Generate comprehensive local business schema with city-specific data
  const schemaData: any = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://ricksblackcarservicesantonio.com${canonicalPath}#localbusiness`,
    "name": `Rick's Black Car Service - ${cityName}`,
    "description": metaDescription,
    "url": `https://ricksblackcarservicesantonio.com${canonicalPath}`,
    "telephone": "+1-210-296-7409",
    "email": "rick@ricksblackcarservicesantonio.com",
    "priceRange": "$$-$$$",
    "image": {
      "@type": "ImageObject",
      "@id": `https://ricksblackcarservicesantonio.com${canonicalPath}#heroimage`,
      "url": heroImageUrl,
      "contentUrl": heroImageUrl,
      "name": `${cityName} Black Car Service - Premium Transportation`,
      "caption": `Professional black car and chauffeur service in ${cityName}, Texas`,
      "description": `Luxury black car service in ${cityName}, TX. Airport transfers, corporate travel, weddings, and special events with professional chauffeur.`,
      "width": 1920,
      "height": 1080,
      "encodingFormat": "image/jpeg",
      "representativeOfPage": true,
      "license": "https://ricksblackcarservicesantonio.com",
      "acquireLicensePage": "https://ricksblackcarservicesantonio.com/contact",
      "creditText": "Rick's Black Car Service",
      "copyrightNotice": "© Rick's Black Car Service"
    },
    "logo": {
      "@type": "ImageObject",
      "url": "https://ricksblackcarservicesantonio.com/og-image.jpg",
      "width": 1200,
      "height": 630
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": cityName,
      "addressRegion": "TX",
      "addressCountry": "US"
    },
    "geo": coordinates ? {
      "@type": "GeoCoordinates",
      "latitude": coordinates.latitude,
      "longitude": coordinates.longitude
    } : undefined,
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": coordinates ? {
        "@type": "GeoCoordinates",
        "latitude": coordinates.latitude,
        "longitude": coordinates.longitude
      } : {
        "@type": "GeoCoordinates",
        "latitude": 29.4241,
        "longitude": -98.4936
      },
      "geoRadius": serviceRadius || "50 miles",
      "name": `${cityName}, Texas and surrounding areas`
    },
    "serviceArea": [
      {
        "@type": "City",
        "name": cityName,
        "addressRegion": "Texas"
      },
      {
        "@type": "City",
        "name": "San Antonio",
        "addressRegion": "Texas"
      }
    ],
    "parentOrganization": {
      "@type": "Organization",
      "@id": "https://ricksblackcarservicesantonio.com/#organization",
      "name": "Rick's Black Car Service",
      "url": "https://ricksblackcarservicesantonio.com"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-210-296-7409",
      "contactType": "Customer Service",
      "areaServed": "TX",
      "availableLanguage": ["English"],
      "hoursAvailable": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "00:00",
        "closes": "23:59"
      }
    },
    "sameAs": [
      "https://share.google/GtRFwnU1H9WWSjG5l"
    ],
    "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "Invoice"],
    "currenciesAccepted": "USD",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": cityReviews?.length || 5,
      "bestRating": "5",
      "worstRating": "1"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
      ],
      "opens": "00:00",
      "closes": "23:59"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `Black Car Services in ${cityName}`,
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Airport Transportation",
            "description": featuredPricing 
              ? `Professional SAT airport transfers from ${cityName}. ${featuredPricing.route} - ${featuredPricing.price} flat rate (${featuredPricing.note || 'gratuity included'})`
              : `Professional SAT airport transfers from ${cityName}`,
            "provider": {
              "@type": "LocalBusiness",
              "name": `Rick's Black Car Service - ${cityName}`
            },
            "areaServed": {
              "@type": "City",
              "name": cityName,
              "addressRegion": "Texas"
            }
          },
          ...(featuredPricing && {
            "price": featuredPricing.price.replace('$', ''),
            "priceCurrency": "USD",
            "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
            "eligibleRegion": {
              "@type": "Place",
              "name": `${cityName}, Texas`
            }
          }),
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Corporate Travel",
            "description": `Executive transportation for business travelers in ${cityName}`,
            "provider": {
              "@type": "LocalBusiness",
              "name": `Rick's Black Car Service - ${cityName}`
            },
            "areaServed": {
              "@type": "City",
              "name": cityName,
              "addressRegion": "Texas"
            }
          },
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Special Events",
            "description": `Luxury transportation for weddings and special events in ${cityName}`,
            "provider": {
              "@type": "LocalBusiness",
              "name": `Rick's Black Car Service - ${cityName}`
            },
            "areaServed": {
              "@type": "City",
              "name": cityName,
              "addressRegion": "Texas"
            }
          },
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Hourly Service",
            "description": `Flexible hourly chauffeur service in ${cityName}`,
            "provider": {
              "@type": "LocalBusiness",
              "name": `Rick's Black Car Service - ${cityName}`
            },
            "areaServed": {
              "@type": "City",
              "name": cityName,
              "addressRegion": "Texas"
            }
          },
          "availability": "https://schema.org/InStock"
        }
      ]
    },
    "knowsAbout": [
      "Airport Transportation",
      "Corporate Transportation",
      "Wedding Transportation",
      "Luxury Black Car Service",
      "Chauffeur Service",
      `${cityName} Transportation`
    ]
  };

  // Add city-specific reviews if provided
  if (cityReviews && cityReviews.length > 0) {
    schemaData.review = cityReviews.map(review => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating.toString(),
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": review.author
      },
      "datePublished": review.date,
      "reviewBody": review.text
    }));
  }

  return (
    <>
      <SEO 
        title={`${cityName} Car Service | Rick's Black Car San Antonio`}
        description={metaDescription}
        canonicalPath={canonicalPath}
        schema={schemaData}
      />
      
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb 
            items={[
              { name: "Service Areas", url: "/service-areas" },
              { name: `${cityName} Black Car Service`, url: canonicalPath }
            ]}
          />
          {/* Hero Section */}
          <section className="relative py-20 overflow-hidden">
            {heroImage && (
              <>
                <img 
                  src={heroImage}
                  alt={`Premium black car service in ${cityName}, Texas - Luxury chauffeur transportation`}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                  width={1920}
                  height={1080}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background/90" />
              </>
            )}
            {!heroImage && <div className="absolute inset-0 luxury-gradient" />}
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-6">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground">
                  {cityName} Black Car Service
                </h1>
                <p className="text-xl text-muted-foreground">
                  {cityDescription}
                </p>
                
                {/* Featured Pricing Highlight */}
                {featuredPricing && (
                  <div className="bg-primary/10 border-2 border-primary rounded-xl p-6 max-w-md mx-auto">
                    <p className="text-sm uppercase tracking-wider text-primary font-semibold mb-2">Featured Flat Rate</p>
                    <p className="text-3xl md:text-4xl font-bold text-primary mb-2">{featuredPricing.price}</p>
                    <p className="text-lg font-medium text-foreground">{featuredPricing.route}</p>
                    {featuredPricing.note && (
                      <p className="text-sm text-muted-foreground mt-2">{featuredPricing.note}</p>
                    )}
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                  <Button className="btn-luxury text-lg px-8 py-6" asChild>
                    <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                      Book Now
                    </a>
                  </Button>
                  <Button variant="outline" className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                    <a href="tel:+12102967409">
                      <Phone className="w-5 h-5 mr-2" />
                      Call: 210-296-7409
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                  Premium Services for {cityName}
                </h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  Professional chauffeur service tailored to your needs.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {services.map((service, index) => (
                  <Card key={index} className="card-luxury">
                    <CardContent className="p-6 space-y-3 text-center">
                      <div className="text-primary mx-auto w-fit">{service.icon}</div>
                      <h3 className="text-lg font-semibold text-foreground">{service.title}</h3>
                      <p className="text-muted-foreground text-sm">{service.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Details Section */}
          <section className="py-20 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-8">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground text-center mb-8">
                  Why Choose Rick's Black Car Service in {cityName}?
                </h2>

                <div className="space-y-6 text-muted-foreground">
                  {additionalContent && (
                    <p className="text-lg leading-relaxed">{additionalContent}</p>
                  )}
                  
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-foreground mb-2">Professional Chauffeurs</h3>
                      <p>Our experienced, licensed chauffeurs provide safe, courteous, and discreet service. Every driver is background-checked and professionally trained.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-foreground mb-2">Luxury Fleet</h3>
                      <p>Travel in style with our meticulously maintained fleet of premium vehicles including Mercedes-Benz, BMW, Audi, and Cadillac models.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-foreground mb-2">24/7 Availability</h3>
                      <p>We're always available when you need us. Early morning pickups, late-night returns, or last-minute bookings—we've got you covered.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-foreground mb-2">Competitive Pricing</h3>
                      <p>Enjoy premium service at fair, transparent rates with no hidden fees. Get an instant quote when you book online.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Reviews Section */}
          {cityReviews && cityReviews.length > 0 && (
            <section className="py-20">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    What Our {cityName} Customers Say
                  </h2>
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-6 h-6 text-primary fill-primary" />
                      ))}
                    </div>
                    <span className="text-muted-foreground text-lg">5.0 Rating</span>
                  </div>
                  <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                    Read verified reviews from our satisfied customers in {cityName}.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                  {cityReviews.map((review, index) => (
                    <Card key={index} className="card-luxury">
                      <CardContent className="p-6 space-y-4">
                        <div className="flex items-center gap-1">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} className="w-5 h-5 text-primary fill-primary" />
                          ))}
                        </div>
                        <p className="text-muted-foreground italic">"{review.text}"</p>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-foreground">{review.author}</span>
                          <span className="text-sm text-muted-foreground">
                            {new Date(review.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </section>
          )}


          {/* CTA Section */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-8">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
                  Ready to Book Your Ride in {cityName}?
                </h2>
                <p className="text-muted-foreground text-lg">
                  Experience the Rick's Black Car Service difference. Book online or call us today.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button className="btn-luxury text-lg px-8 py-6" asChild>
                    <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                      Book Now
                    </a>
                  </Button>
                  <Button variant="outline" className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                    <a href="tel:+12102967409">
                      <Phone className="w-5 h-5 mr-2" />
                      Call: 210-296-7409
                    </a>
                  </Button>
                </div>
              </div>
            </div>
        </section>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />

          {/* Mobile Sticky Bar */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t border-border/50 shadow-lg">
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-center gap-3">
                <Button className="btn-luxury flex-1 max-w-[200px]" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call
                  </a>
                </Button>
                <Button className="btn-luxury flex-1 max-w-[200px]" asChild>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    Book Now
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default CityServiceTemplate;
